/**
 * Candidate forms of a step's stored anchor path, most specific first.
 *
 * The layout stamps a page's content host with a page-root help-path
 * (`<appSlug>-<leaf>`) on NAVIGATION, so an anchor renders as
 * `sparta-timekeeping-page/timekeeping__header` when the user arrived by
 * client-side nav but as bare `timekeeping__header` on a direct page load — and a
 * tour stores whichever form was live when it was AUTHORED. Matching only the
 * stored form breaks every element step after a nav step, in both directions.
 *
 * So a stored path yields two candidates: the path as stored, and the path minus
 * a leading page-root segment. A leading segment counts as a page-root stamp only
 * when it lacks the `__` of anchor ids (`timekeeping__header`), so a bare
 * structural path like `timekeeping__tab-strip/timekeeping__by-job` is never
 * loosened.
 *
 * Each candidate is matched by {@link anchorPathMatches}, which covers the other
 * half of the race (stored bare, rendered stamped).
 */
export function anchorPathVariants(path: string): string[] {
  const variants = [path];
  const slash = path.indexOf('/');
  if (slash > 0 && !path.slice(0, slash).includes('__')) {
    variants.push(path.slice(slash + 1));
  }
  return variants;
}

/**
 * Whether a rendered anchor's help-path satisfies one candidate from
 * {@link anchorPathVariants} — exactly, or as a trailing `/`-segment run (the
 * rendered path carries a page-root prefix the stored path does not).
 *
 * Suffix matching is on whole segments: `submit` must not match
 * `form__submit`, only `.../submit`.
 */
export function anchorPathMatches(
  candidate: string,
  renderedPath: string | null | undefined
): boolean {
  if (!renderedPath || !candidate) {
    return false;
  }
  return renderedPath === candidate || renderedPath.endsWith(`/${candidate}`);
}
